import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/ui/dialog';
import { Button } from '@/ui/Button';
import { Input } from '@/ui/input';
import { Textarea } from '@/ui/textarea';
import { Label } from '@/ui/label';
import { Switch } from '@/ui/switch';
import { supabaseService, type CustomRule, type CreateRuleRequest, type Repository } from '@/shared/services/supabaseService';
import { toast } from 'sonner';
import { SelectedReposList } from './SelectedReposList';
import { RepoSelectionCombobox } from './RepoSelectionCombobox';

interface CreateRuleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (rule: CustomRule) => void;
  editingRule?: CustomRule;
}

const CreateRuleDialog: React.FC<CreateRuleDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingRule
}) => {
  const [formData, setFormData] = useState<CreateRuleRequest>({
    title: '',
    description: '',
    rule_content: '',
    is_public: false
  });
  const [loading, setLoading] = useState(false);
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [selectedRepositories, setSelectedRepositories] = useState<string[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (editingRule) {
      setFormData({
        title: editingRule.title,
        description: editingRule.description || '',
        rule_content: editingRule.rule_content,
        is_public: editingRule.is_public
      });
    } else {
      setFormData({
        title: '',
        description: '',
        rule_content: '',
        is_public: false
      });
    }
  }, [editingRule, isOpen]);

  // Fetch repositories when dialog opens
  useEffect(() => {
    if (isOpen) {
      const fetchRepositories = async () => {
        try {
          const repos = await supabaseService.getRepositories();
          setRepositories(repos);

          // If editing a rule, efficiently fetch which repositories have this rule
          if (editingRule) {
            const reposWithRule = await supabaseService.getRepositoriesForRule(editingRule.id);
            setSelectedRepositories(reposWithRule);
          } else {
            setSelectedRepositories([]);
          }
        } catch (error) {
          console.error('Error fetching repositories:', error);
        }
      };
      fetchRepositories();
    }
  }, [isOpen, editingRule]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.rule_content.trim()) {
      toast.error('Title and rule content are required');
      return;
    }

    setLoading(true);
    try {
      let rule: CustomRule;

      if (editingRule) {
        rule = await supabaseService.updateCustomRule(editingRule.id, formData);
        toast.success('Rule updated successfully');

        // Handle repository changes for editing - use efficient method
        const currentRepoIds = await supabaseService.getRepositoriesForRule(editingRule.id);

        // Find repositories to add and remove
        const reposToAdd = selectedRepositories.filter(id => !currentRepoIds.includes(id));
        const reposToRemove = currentRepoIds.filter(id => !selectedRepositories.includes(id));

        // Add rule to new repositories
        if (reposToAdd.length > 0) {
          await Promise.all(
            reposToAdd.map(repoId =>
              supabaseService.addRuleToRepository(repoId, rule.id)
            )
          );
        }

        // Remove rule from repositories
        if (reposToRemove.length > 0) {
          await Promise.all(
            reposToRemove.map(repoId =>
              supabaseService.removeRuleFromRepository(repoId, rule.id)
            )
          );
        }

        if (reposToAdd.length > 0 || reposToRemove.length > 0) {
          toast.success(`Rule repository assignments updated`);
        }
      } else {
        rule = await supabaseService.createCustomRule(formData);
        toast.success('Rule created successfully');

        // Add rule to selected repositories
        if (selectedRepositories.length > 0) {
          await Promise.all(
            selectedRepositories.map(repoId =>
              supabaseService.addRuleToRepository(repoId, rule.id)
            )
          );
          toast.success(`Rule added to ${selectedRepositories.length} repository(ies)`);
        }
      }

      onSuccess(rule);
      onClose();
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error('Failed to save rule');
    } finally {
      setLoading(false);
    }
  };

  const toggleRepoSelection = (repoId: string) => {
    setSelectedRepositories(prev =>
        prev.includes(repoId)
            ? prev.filter(id => id !== repoId)
            : [...prev, repoId]
    );
  };

  const removeRepo = (repoId: string) => {
      setSelectedRepositories(prev => prev.filter(id => id !== repoId));
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none border-2 border-neutral-950 dark:border-neutral-100 p-0 gap-0 bg-background">
        <DialogHeader className="p-6 border-b border-neutral-950 dark:border-neutral-100 bg-neutral-50 dark:bg-neutral-900/10">
          <DialogTitle className="font-mono text-xl uppercase font-bold tracking-tight">
            {editingRule ? 'Edit Rule' : 'Create New Rule'}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-muted-foreground mt-2">
            {editingRule
              ? 'Update your custom rule details below.'
              : 'Create a new custom rule that can be reused across repositories.'
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="font-mono text-xs uppercase font-bold">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="E.G., REACT_BEST_PRACTICES"
              required
              className="rounded-none font-mono text-xs border-neutral-950 dark:border-neutral-100 focus-visible:ring-0"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="font-mono text-xs uppercase font-bold">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="BRIEF_DESCRIPTION"
              className="rounded-none font-mono text-xs border-neutral-950 dark:border-neutral-100 focus-visible:ring-0"
            />
          </div>


          {/* Rule Content */}
          <div className="space-y-2">
            <Label htmlFor="rule_content" className="font-mono text-xs uppercase font-bold">Rule Content *</Label>
            <Textarea
              id="rule_content"
              value={formData.rule_content}
              onChange={(e) => setFormData(prev => ({ ...prev, rule_content: e.target.value }))}
              placeholder="Describe the rule in detail..."
              rows={6}
              required
              className="rounded-none font-mono text-xs border-neutral-950 dark:border-neutral-100 focus-visible:ring-0 resize-none p-3"
            />
          </div>


          {/* Repository Selection */}
          <div className="space-y-4 pt-4 border-t border-dashed border-neutral-300 dark:border-neutral-700">
            <div>
                <Label className="font-mono text-xs uppercase font-bold">Attach to Repositories (Optional)</Label>
                <p className="font-mono text-[10px] text-muted-foreground mt-1">
                {editingRule
                    ? "Changes will be saved when you update the rule."
                    : "Select repositories where this rule should be automatically applied"
                }
                </p>
            </div>

            <SelectedReposList
                repositories={repositories}
                selectedRepositories={selectedRepositories}
                onRemove={removeRepo}
            />

            <RepoSelectionCombobox
                repositories={repositories}
                selectedRepositories={selectedRepositories}
                onSelect={toggleRepoSelection}
                open={popoverOpen}
                setOpen={setPopoverOpen}
            />
          </div>

          {/* Options */}
          <div className="space-y-4 pt-4 border-t border-dashed border-neutral-300 dark:border-neutral-700">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="is_public" className="font-mono text-xs uppercase font-bold">Public Rule</Label>
                <p className="font-mono text-[10px] text-muted-foreground">
                  Make this rule available to all users
                </p>
              </div>
              <Switch
                id="is_public"
                checked={formData.is_public}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public: checked }))}
                className="data-[state=checked]:bg-green-600 dark:data-[state=checked]:bg-green-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-neutral-100 dark:border-neutral-800">
            <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="rounded-none font-mono uppercase text-xs border-neutral-200 dark:border-neutral-800"
            >
              Cancel
            </Button>
            <Button
                type="submit"
                disabled={loading}
                className="rounded-none font-mono uppercase text-xs font-bold bg-neutral-950 text-white dark:bg-white dark:text-neutral-950 hover:bg-neutral-800 dark:hover:bg-neutral-200"
            >
              {loading ? 'SAVING...' : (editingRule ? '[ UPDATE_RULE ]' : '[ CREATE_RULE ]')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRuleDialog;
